import { Box, Dialog, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Button } from 'shared/ui/Button';
import { Paper } from 'shared/ui/Paper';

interface Props {
	backupCodes: string[] | null;
	onConfirm: () => void;
}

export const TwoFactorBackupCodes: FunctionComponent<Props> = ({ backupCodes, onConfirm }) => {
	const { t } = useTranslation();
	const codesText = backupCodes?.join('\n') ?? '';

	const handleCopy = () => {
		navigator.clipboard
			.writeText(codesText)
			.then(() => enqueueSnackbar({ message: t('auth.two_factor.copied'), variant: 'success' }))
			.catch(() => enqueueSnackbar({ message: t('shared_texts.smth_went_wrong'), variant: 'error' }));
	};

	const handleDownload = () => {
		const url = window.URL.createObjectURL(new Blob([`${codesText}\n`], { type: 'text/plain' }));
		const a = document.createElement('a');
		a.href = url;
		a.download = 'retica-backup-codes.txt';
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	};

	return (
		<Dialog
			open={!!backupCodes}
			PaperComponent={Paper}
			PaperProps={{ title: t('auth.two_factor.backup_codes_title') as string }}
		>
			<Typography
				variant={'body2'}
				sx={{
					marginBottom: '24px',
					padding: '12px 16px',
					borderRadius: '10px',
					color: ColorPalette.violetRed,
					border: `1px solid ${ColorPalette.violetRed}`,
				}}
			>
				{t('auth.two_factor.backup_codes_warning')}
			</Typography>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(2, 1fr)',
					gap: '12px',
					marginBottom: '24px',
					padding: '16px',
					borderRadius: '10px',
					border: `1px solid ${ColorPalette.periwinkle}`,
				}}
			>
				{backupCodes?.map((code) => (
					<Typography
						key={code}
						align={'center'}
						color={ColorPalette.astronaut}
						sx={{
							fontFamily: 'monospace',
							fontSize: '16px',
							fontWeight: 600,
							letterSpacing: '1px',
						}}
					>
						{code}
					</Typography>
				))}
			</Box>

			<Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
				<Button variant={'outlined'} onClick={handleCopy} fullWidth>
					{t('auth.two_factor.copy')}
				</Button>
				<Button variant={'outlined'} onClick={handleDownload} fullWidth>
					{t('auth.two_factor.download_txt')}
				</Button>
			</Box>

			<Button onClick={onConfirm} fullWidth>
				{t('auth.two_factor.codes_saved')}
			</Button>
		</Dialog>
	);
};
